import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { 
  FaCommentDots, FaTimes, FaPaperPlane, FaWhatsapp, 
  FaTelegram, FaChevronDown, FaChevronUp, FaRobot,
  FaExternalLinkAlt
} from 'react-icons/fa';
import apiClient from '../../api/apiClient';
import { useAuth } from '../../contexts/AuthContext';

const ChatBot = () => {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [quickQuestions, setQuickQuestions] = useState([]);
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [showQuickQuestions, setShowQuickQuestions] = useState(true);
  const messagesEndRef = useRef(null);

  // Charger les questions rapides et le message de bienvenue
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      loadWelcomeMessage();
      loadQuickQuestions();
    }
  }, [isOpen]);

  const loadWelcomeMessage = async () => {
    try {
      const language = i18n.language?.split('-')[0] || 'fr';
      const params = { language };
      if (user?.firstName) params.userName = user.firstName;
      
      const response = await apiClient.get('/bot/welcome', { params });
      if (response.data?.success) {
        setMessages([{
          id: Date.now(),
          text: response.data.reply,
          sender: 'bot',
          actions: response.data.actions || []
        }]);
      }
    } catch (error) {
      // Fallback
      setMessages([{
        id: Date.now(),
        text: "Bonjour ! 👋 Je suis Baobab Bot, votre assistant virtuel. Comment puis-je vous aider ?",
        sender: 'bot',
        actions: []
      }]);
    }
  };

  const loadQuickQuestions = async () => {
    try {
      const language = i18n.language?.split('-')[0] || 'fr';
      const response = await apiClient.get('/bot/quick-questions', { params: { language } });
      if (response.data?.success) {
        setQuickQuestions(response.data.data);
      }
    } catch (error) {
      // Fallback questions
      setQuickQuestions([
        {
          category: '💸 Transferts',
          questions: ['Comment faire un transfert ?', 'Quels sont les délais ?']
        },
        {
          category: '💰 Frais',
          questions: ['Quels sont les frais ?', 'Quel est le taux de change ?']
        }
      ]);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(scrollToBottom, [messages]);

  const handleSendMessage = async (e, quickQuestion = null) => {
    if (e) e.preventDefault();
    const messageText = quickQuestion || inputValue;

    if (!messageText.trim()) return;

    const userMessage = { 
      id: Date.now(), 
      text: messageText, 
      sender: 'user',
      actions: []
    };
    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);
    setShowQuickQuestions(false);

    try {
      const language = i18n.language?.split('-')[0] || 'fr';
      const response = await apiClient.post('/bot/chat', { 
        message: messageText,
        language 
      });
      
      if (response.data?.success) {
        const botMessage = { 
          id: Date.now() + 1, 
          text: response.data.reply, 
          sender: 'bot',
          actions: response.data.actions || [],
          category: response.data.category,
          sentiment: response.data.sentiment
        };
        setMessages(prev => [...prev, botMessage]);
      }
    } catch (error) {
      const errorMessage = {
        id: Date.now() + 1,
        text: "Désolé, je rencontre un problème technique. Contactez-nous sur WhatsApp ou Telegram.",
        sender: 'bot',
        actions: [
          { label: 'WhatsApp', url: 'https://wa.me/79511370604', type: 'external' },
          { label: 'Telegram', url: 'https://t.me/baobabexchange', type: 'external' }
        ]
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsTyping(false);
    }
  };

  const handleActionClick = (action) => {
    if (action.type === 'external') {
      window.open(action.url, '_blank', 'noopener,noreferrer');
    } else if (action.type === 'link') {
      navigate(action.url);
      setIsOpen(false);
    }
  };

  const toggleCategory = (categoryIndex) => {
    setExpandedCategory(expandedCategory === categoryIndex ? null : categoryIndex);
  };

  const renderMessage = (msg) => {
    const isBot = msg.sender === 'bot';
    
    return (
      <div key={msg.id} className={`flex ${isBot ? 'justify-start' : 'justify-end'} mb-3`}>
        <div className={`max-w-[85%] ${isBot ? 'order-2' : ''}`}>
          {isBot && (
            <div className="flex items-center gap-1.5 mb-1">
              <div className="w-6 h-6 bg-gradient-to-br from-green-500 to-green-600 rounded-full flex items-center justify-center">
                <FaRobot className="text-white text-xs" />
              </div>
              <span className="text-xs text-slate-400">Baobab Bot</span>
            </div>
          )}
          <div
            className={`px-4 py-3 rounded-2xl ${
              isBot 
                ? 'bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 border border-slate-200 dark:border-slate-700 rounded-tl-sm' 
                : 'bg-gradient-to-r from-green-500 to-green-600 text-white rounded-tr-sm'
            }`}
          >
            <div className="text-sm whitespace-pre-line">{msg.text}</div>
            
            {/* Actions/Boutons */}
            {msg.actions && msg.actions.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-3">
                {msg.actions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleActionClick(action)}
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all
                      ${action.type === 'external' 
                        ? 'bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-200 dark:hover:bg-slate-600' 
                        : 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 hover:bg-green-200 dark:hover:bg-green-900/50'
                      }`}
                  >
                    {action.label}
                    {action.type === 'external' && <FaExternalLinkAlt className="text-[10px]" />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      {/* Bouton flottant */}
      <div className="fixed bottom-6 right-6 z-50">
        <motion.button
          onClick={() => setIsOpen(!isOpen)}
          className="relative bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full p-4 shadow-lg transition-all duration-200"
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          {isOpen ? <FaTimes size={22} /> : <FaCommentDots size={22} />}
          {!isOpen && (
            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full animate-pulse" />
          )}
        </motion.button>
      </div>

      {/* Fenêtre de chat */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="fixed bottom-24 right-6 w-[380px] h-[600px] bg-slate-50 dark:bg-slate-900 rounded-2xl shadow-2xl flex flex-col z-50 overflow-hidden border border-slate-200 dark:border-slate-800"
          >
            {/* Header */}
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white p-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <FaRobot size={20} />
                </div>
                <div>
                  <h3 className="font-bold text-lg">Baobab Assistant</h3>
                  <p className="text-xs text-green-100">En ligne • Répond instantanément</p>
                </div>
              </div>
            </div>

            {/* Zone de messages */}
            <div className="flex-1 p-4 overflow-y-auto bg-slate-100 dark:bg-slate-950">
              {messages.map(renderMessage)}
              
              {/* Indicateur de frappe */}
              {isTyping && (
                <div className="flex justify-start mb-3">
                  <div className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-2xl rounded-tl-sm px-4 py-3">
                    <div className="flex items-center gap-1">
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" />
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0.15s'}} />
                      <span className="w-2 h-2 bg-green-500 rounded-full animate-bounce" style={{animationDelay: '0.3s'}} />
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            {/* Questions rapides */}
            {showQuickQuestions && quickQuestions.length > 0 && (
              <div className="px-4 py-3 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 max-h-[180px] overflow-y-auto">
                <p className="text-xs text-slate-500 dark:text-slate-400 mb-2 font-medium">
                  Questions fréquentes :
                </p>
                <div className="space-y-2">
                  {quickQuestions.map((cat, catIdx) => (
                    <div key={catIdx}>
                      <button
                        onClick={() => toggleCategory(catIdx)}
                        className="w-full flex items-center justify-between text-xs font-medium text-slate-700 dark:text-slate-300 py-1.5 hover:text-green-600 dark:hover:text-green-400"
                      >
                        <span>{cat.category}</span>
                        {expandedCategory === catIdx ? <FaChevronUp /> : <FaChevronDown />}
                      </button>
                      {expandedCategory === catIdx && (
                        <div className="pl-2 space-y-1 mt-1">
                          {cat.questions.map((q, qIdx) => (
                            <button
                              key={qIdx}
                              onClick={(e) => handleSendMessage(e, q)}
                              className="block w-full text-left text-xs text-slate-600 dark:text-slate-400 py-1.5 px-2 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                              {q}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Input et boutons de contact */}
            <div className="p-4 bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800">
              {/* Boutons de contact rapide */}
              <div className="flex gap-2 mb-3">
                <a
                  href="https://wa.me/79511370604"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 rounded-lg text-xs font-medium hover:bg-green-200 dark:hover:bg-green-900/50 transition-colors"
                >
                  <FaWhatsapp />
                  WhatsApp
                </a>
                <a
                  href="https://t.me/baobabexchange"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1 flex items-center justify-center gap-2 py-2 px-3 bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400 rounded-lg text-xs font-medium hover:bg-sky-200 dark:hover:bg-sky-900/50 transition-colors"
                >
                  <FaTelegram />
                  Telegram
                </a>
              </div>
              
              {/* Formulaire de message */}
              <form onSubmit={handleSendMessage} className="flex items-center gap-2">
                <input
                  type="text"
                  value={inputValue}
                  onChange={(e) => setInputValue(e.target.value)}
                  placeholder={t('chatbot.placeholder', 'Écrivez votre message...')}
                  className="flex-1 bg-slate-100 dark:bg-slate-800 rounded-xl py-3 px-4 text-sm text-slate-900 dark:text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-green-500 border border-slate-200 dark:border-slate-700"
                />
                <button 
                  type="submit" 
                  className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-xl p-3 transition-all disabled:opacity-50" 
                  disabled={isTyping || !inputValue.trim()}
                >
                  <FaPaperPlane size={16} />
                </button>
              </form>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default ChatBot;
